import { useRef } from 'react';
import { useFramework, useScale } from '@/framework';
import { getLayerInfoWithRotate, sampleLayersUniformly } from '@/utils/layer-kit';
import { DirectionType, ScaleOperateType, type ILayerProps, type IPoint } from '@/version-types';
import { SmartGuidesPlugin } from '../../smart-guides';
import { SCALE_ABSORB_DISTANCE } from '../constants';
import LineList from '../tools/line-list';
import { checkLayerNeedToAlign, getDirectionVector, getScaleAbsorbOffset } from '../tools/utils';

export const useScaleSnap = () => {
    const framework = useFramework();
    const scale = useScale();
    const scaleRef = useRef(scale);
    scaleRef.current = scale;

    // 是否吸附中
    const isAdsorb = useRef<boolean>(false);
    // 吸附状态下的位移
    const adsorbDisplacement = useRef<IPoint>({ x: 0, y: 0 });

    const lineData = useRef<LineList>();

    const proxyBeforeScaling = (operateType: ScaleOperateType, layer?: ILayerProps | null) => {
        if (!layer) return;

        lineData.current = new LineList();

        if (!framework.service.edgelessCanvas.isEdgelessMode) {
            const { width, height } = framework.service.frame.getActiveFrameSize();
            // 舞台边缘
            lineData.current.push({
                width,
                height,
                left: 0,
                top: 0,
                rotate: 0,
                isStage: true,
                id: 'stage_' + Date.now(),
            });
        }

        let layers: ILayerProps[] = [];
        if (layer.parentId) {
            const warpLayer = framework.service.flatLayer.getWrapLayer(layer.id);
            if (warpLayer) {
                layers = framework.service.flatLayer.getChildrenLayers(warpLayer.id);
            }
        } else {
            const originIndex = layer.__originIndex ?? framework.service.frame.getActiveFrameId();
            const activeLayerIds = framework.service.frame.getActiveLayerIds();
            const frame = framework.service.frame.get(originIndex);
            const transform = framework.service.frame.getFrameTransform(originIndex);
            if (frame) {
                // 舞台边缘
                lineData.current.push({
                    width: frame.width,
                    height: frame.height,
                    left: frame.left,
                    top: frame.top - transform.offsetTop,
                    rotate: 0,
                    isStage: true,
                    id: 'stage_' + Date.now(),
                });
            }
            layers = framework.service.frame.getFlatLayers(originIndex, activeLayerIds);
        }

        // 过滤不在可视范围内的图层
        layers = layers.filter((layer) => {
            return (
                framework.service.edgelessCanvas.getByLayerId(layer.id)?.checkInViewport().intersects &&
                !framework.service.flatLayer.checkIsNeedHidden(layer)
            );
        });

        const directionVector = getDirectionVector(operateType, layer.rotate);
        const layerRect = getLayerInfoWithRotate(layer);
        // 添加参与计算的图层
        sampleLayersUniformly(layers, layer, false).forEach((item) => {
            const checkLayerRect = getLayerInfoWithRotate(item);
            const needToAlign = checkLayerNeedToAlign(layerRect, checkLayerRect, directionVector);
            if (needToAlign) {
                lineData.current!.push(item);
            }
        });
    };
    const proxyScaling = ({
        displacementX,
        displacementY,
        offsetX,
        offsetY,
        directionX,
        directionY,
        oldLayer,
        operateType,
        keepRatio,
        layer,
    }: {
        displacementX: number;
        displacementY: number;
        offsetX: number;
        offsetY: number;
        directionX: DirectionType;
        directionY: DirectionType;
        oldLayer: ILayerProps;
        operateType: ScaleOperateType;
        keepRatio: boolean;
        /** MouseOperate 在本帧解析的实际操作图层。 */
        layer?: ILayerProps | null;
    }) => {
        if (!layer) return { x: 0, y: 0 };

        const rotate = layer.rotate;

        /**
         * 求出当前缩放值下, layer 宽高的变化, 用于 block 的计算
         * 因为拖动图层的左边或者上边调整的时候, 会改变图层的 left, top
         * 所以需要用调整后的 left, top 去计算才会准确
         */
        const leftOffset = directionX === DirectionType.OPPOSITE ? displacementX * directionX : 0;
        const topOffset = directionY === DirectionType.OPPOSITE ? displacementY * directionY : 0;

        // 当前鼠标调整后的图层尺寸(不包含吸附)
        const resizedLayer = {
            ...oldLayer,
            left: oldLayer.left + leftOffset,
            top: oldLayer.top + topOffset,
            width: Math.floor(oldLayer.width + displacementX),
            height: Math.floor(oldLayer.height + displacementY),
        };

        const oldLayerRect = getLayerInfoWithRotate(oldLayer);
        // 需要基于 resize 前的中心点进行旋转
        const centerPoint = {
            x: oldLayer.width / 2 + oldLayer.left,
            y: oldLayer.height / 2 + oldLayer.top,
        };
        const newLayerRect = getLayerInfoWithRotate(resizedLayer, centerPoint);

        const directionVector = getDirectionVector(operateType, rotate);

        const lineNearest = lineData.current?.getNearestLineForScale(
            {
                ...newLayerRect,
                rotate: 0,
            },
            directionVector,
        );

        if (lineNearest) {
            const adsorbDistance = Math.max(1, Math.ceil(SCALE_ABSORB_DISTANCE / scaleRef.current));
            // x 轴吸附位移, 向左为负, 向右为正
            const nearestOffsetX = -lineNearest.vertical.distance;
            // y 轴吸附位移, 向上为负, 向下为正
            const nearestOffsetY = -lineNearest.horizontal.distance;
            // 进入吸附范围
            if (Math.abs(nearestOffsetX) < adsorbDistance || Math.abs(nearestOffsetY) < adsorbDistance) {
                if (!keepRatio) {
                    if (Math.abs(nearestOffsetX) < Math.abs(nearestOffsetY)) {
                        adsorbDisplacement.current.x = offsetX + nearestOffsetX;
                        adsorbDisplacement.current.y = offsetY;
                    } else {
                        adsorbDisplacement.current.x = offsetX;
                        adsorbDisplacement.current.y = offsetY + nearestOffsetY;
                    }
                    isAdsorb.current = true;
                } else if (!isAdsorb.current) {
                    let scaleOffset = { x: 0, y: 0 };

                    // 上下吸附
                    if (Math.abs(nearestOffsetX) < Math.abs(nearestOffsetY)) {
                        const directionX = directionVector.x === 0 ? 0 : directionVector.x < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.width + nearestOffsetX * directionX) - oldLayerRect.width;
                        scaleOffset = getScaleAbsorbOffset({
                            rotate,
                            isHorizontal: false,
                            additionalRectSize,
                            oldLayer,
                            oldLayerRect,
                            mouseOffsetX: displacementX,
                            mouseOffsetY: displacementY,
                        });
                    }
                    // 左右吸附
                    else {
                        const directionY = directionVector.y === 0 ? 0 : directionVector.y < 0 ? -1 : 1;
                        // 吸附的偏移量
                        const additionalRectSize =
                            Math.abs(newLayerRect.height + nearestOffsetY * directionY) - oldLayerRect.height;
                        scaleOffset = getScaleAbsorbOffset({
                            rotate,
                            isHorizontal: true,
                            additionalRectSize,
                            oldLayer,
                            oldLayerRect,
                            mouseOffsetX: displacementX,
                            mouseOffsetY: displacementY,
                        });
                    }
                    adsorbDisplacement.current.y = scaleOffset.y;
                    adsorbDisplacement.current.x = scaleOffset.x;
                    isAdsorb.current = true;
                }
            } else {
                adsorbDisplacement.current.x = displacementX;
                adsorbDisplacement.current.y = displacementY;
                isAdsorb.current = false;
            }
        } else {
            adsorbDisplacement.current.x = displacementX;
            adsorbDisplacement.current.y = displacementY;
        }

        return adsorbDisplacement.current;
    };

    const proxyAfterScaling = (layer: ILayerProps | null | undefined, isEnd: boolean) => {
        if (layer) {
            // 对齐 UI 展示
            const lines = lineData.current?.getLines(layer, true);
            if (lines) {
                SmartGuidesPlugin.get(framework)?.setLines(lines);
            }
        }

        // 清除临时数据
        if (isEnd) {
            lineData.current = undefined;
            isAdsorb.current = false;
        }
    };

    return {
        proxyScaling,
        proxyBeforeScaling,
        proxyAfterScaling,
        resetScaleSnap() {
            lineData.current = undefined;
            isAdsorb.current = false;
        },
    };
};
